import { max } from 'lodash-es';
import moment from 'moment';
import { generatePath } from 'react-router';

import { TPRM_INTAKE, UCRM_INTAKE } from 'Config/PathTemplates';
import { ACTION_CREATED, ACTION_SAVED, ACTION_SKIPPED, ACTION_SUBMITTED, SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS, SEARCH_PARAM_ASSESSMENT_SUBJECT_ID, SEARCH_PARAM_COMPLETING_INTAKE, SEARCH_PARAM_IRQ_ACTIONS, SEARCH_PARAM_ORGANIZATION_ACTIONS, SEARCH_PARAM_ORGANIZATION_ID, SEARCH_PARAM_WORKFLOW_STEP } from 'Config/Text';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceTimeframes, IntakeUrlState, IntakeWorkflowStep, ReassessmentIntervals } from 'Models/DueDiligenceWorkflow';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';

export const buildUrlForIntakeUrlState = (module: DueDiligenceModule, urlState: IntakeUrlState): string => {
    const searchParams = new URLSearchParams();

    if (urlState.completingIntake) {
        searchParams.append(SEARCH_PARAM_COMPLETING_INTAKE, 'true');
    }

    searchParams.append(SEARCH_PARAM_WORKFLOW_STEP, urlState.step.toString());

    if (urlState.organizationId) {
        searchParams.append(SEARCH_PARAM_ORGANIZATION_ID, urlState.organizationId);
    }

    if (urlState.assessmentSubjectId) {
        searchParams.append(SEARCH_PARAM_ASSESSMENT_SUBJECT_ID, urlState.assessmentSubjectId);
    }

    const organizationActions: string[] = [];
    if (urlState.organizationActions.created) {
        organizationActions.push(ACTION_CREATED);
    }
    if (urlState.organizationActions.saved) {
        organizationActions.push(ACTION_SAVED);
    }
    if (urlState.organizationActions.submitted) {
        organizationActions.push(ACTION_SUBMITTED);
    }
    if (organizationActions.length > 0) {
        searchParams.append(SEARCH_PARAM_ORGANIZATION_ACTIONS, organizationActions.join('-'));
    }

    const assessmentSubjectActions: string[] = [];
    if (urlState.assessmentSubjectActions.skipped) {
        assessmentSubjectActions.push(ACTION_SKIPPED);
    }
    if (urlState.assessmentSubjectActions.created) {
        assessmentSubjectActions.push(ACTION_CREATED);
    }
    if (urlState.assessmentSubjectActions.saved) {
        assessmentSubjectActions.push(ACTION_SAVED);
    }
    if (urlState.assessmentSubjectActions.submitted) {
        assessmentSubjectActions.push(ACTION_SUBMITTED);
    }
    if (assessmentSubjectActions.length > 0) {
        searchParams.append(SEARCH_PARAM_ASSESSMENT_SUBJECT_ACTIONS, assessmentSubjectActions.join('-'));
    }

    const irqActions: string[] = [];
    if (urlState.irqActions.skipped) {
        irqActions.push(ACTION_SKIPPED);
    }
    if (urlState.irqActions.saved) {
        irqActions.push(ACTION_SAVED);
    }
    if (urlState.irqActions.submitted) {
        irqActions.push(ACTION_SUBMITTED);
    }
    if (irqActions.length > 0) {
        searchParams.append(SEARCH_PARAM_IRQ_ACTIONS, irqActions.join('-'));
    }

    return `${generatePath(module === ProductModule.UCRM ? UCRM_INTAKE : TPRM_INTAKE, {})}${searchParams.size > 0 ? '?' + searchParams.toString() : ''}`;
};

export const buildUrlForCompletingIntake = (module: DueDiligenceModule, organizationId: string, assessmentSubjectId?: string): string => {
    const urlState: IntakeUrlState = {
        completingIntake: true,
        step: assessmentSubjectId ? IntakeWorkflowStep.CHOOSE_SUBMISSIONS : IntakeWorkflowStep.ASSESSMENT_SUBJECT,
        organizationId,
        assessmentSubjectId,
        organizationActions: { created: false, saved: false, submitted: false },
        assessmentSubjectActions: { created: false, saved: false, submitted: false, skipped: false },
        irqActions: { saved: false, submitted: false, skipped: false },
    };
    return buildUrlForIntakeUrlState(module, urlState);
};

/** Returns the SummIT Guide™ feature that enables due diligence analysis in the given module. */
export const getSummitGuideDueDiligenceFeature = (module: DueDiligenceModule): AdvancedFeature => (module === ProductModule.UCRM ? AdvancedFeature.AI_UCRM_USE_CASE_ASSESSMENT : AdvancedFeature.AI_TPRM_SERVICE_ASSESSMENT);

/** Note that this is the inverse of `getRecommendedIrqTargetDate` in `DueDiligenceFinalReviewSubmitConfirmationModal.tsx`, which works backwards from the workflow due date to the IRQ due date using the same offset (assessments are assumed to be completed in parallel, so the longest assessment duration functions as an offset). */
export const getRecommendedDueDiligenceWorkflowDueDate = (expectedIrqCompletionDate: Date | undefined, assessmentConfigurations: DueDiligenceAssessmentConfiguration[], timeframes: DueDiligenceTimeframes): Date | undefined => {
    const daysToCompleteFinalReview = timeframes.expected_days_to_complete_final_review;
    if (!daysToCompleteFinalReview) {
        return undefined;
    }

    const daysToCompleteAssessmentsInParallel = max(assessmentConfigurations.map((assessment) => (assessment.expected_days_to_complete_questionnaire ?? 0) + (assessment.expected_days_to_complete_assessment ?? 0))) ?? 0;
    return moment(expectedIrqCompletionDate)
        .add(daysToCompleteAssessmentsInParallel + daysToCompleteFinalReview, 'days')
        .toDate();
};

/** Returns the configured number of months between due diligence workflows for an assessment subject with the given inherent risk rating, or `undefined` if the subject is unrated or no interval is configured for its rating. */
export const getRecommendedReassessmentIntervalInMonths = (inherentRiskScore: RiskRating, assessmentIntervals: ReassessmentIntervals): number | undefined => {
    let intervalMonths: number | undefined = undefined;
    switch (inherentRiskScore) {
        case RiskRating.UNRATED:
            intervalMonths = undefined;
            break;
        case RiskRating.LOW:
            intervalMonths = assessmentIntervals.low;
            break;
        case RiskRating.LOW_MODERATE:
            intervalMonths = assessmentIntervals.low_moderate;
            break;
        case RiskRating.MODERATE:
            intervalMonths = assessmentIntervals.moderate;
            break;
        case RiskRating.MODERATE_HIGH:
            intervalMonths = assessmentIntervals.moderate_high;
            break;
        case RiskRating.HIGH:
            intervalMonths = assessmentIntervals.high;
            break;
    }

    return intervalMonths;
};
